import { useState } from 'react';
import { MdAdd, MdSettings } from 'react-icons/md';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import Checkbox from '@/components/Checkbox';
import { Button } from '@/components/Button';
import { Tag, type TagColor } from '@/components/Tag';
import useCompanyTagsList from '../hooks/useCompanyTagsList';
import useCompanyTagChain from '../hooks/useCompanyTagChain';
import useCompanyTagUnchain from '../hooks/useCompanyTagUnchain';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import type { CustomTag } from '../../custom_tags/hooks/useCustomTags';

interface Props {
  id: number;
  tags: CustomTag[];
}

export default function CompanyTagEditDialog({ id, tags }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(tags.map((t) => t.id));
  const [pendingChanges, setPendingChanges] = useState<{
    toAdd: number[];
    toRemove: number[];
  }>({ toAdd: [], toRemove: [] });

  const { data } = useCompanyTagsList();
  const { mutateAsync: mutateChainAsync, isPending: isChaining } = useCompanyTagChain();
  const { mutateAsync: mutateUnchainAsync, isPending: isUnchaining } = useCompanyTagUnchain();
  const queryClient = useQueryClient();

  const isPending = isChaining || isUnchaining;
  const hasChanges = pendingChanges.toAdd.length > 0 || pendingChanges.toRemove.length > 0;

  const handleTagToggle = (tagId: number, checked: boolean) => {
    const originallySelected = tags.some((t) => t.id === tagId);

    setSelectedTagIds((prev) => (checked ? [...prev, tagId] : prev.filter((id) => id !== tagId)));

    setPendingChanges((prev) => {
      if (checked) {
        return originallySelected
          ? { ...prev, toRemove: prev.toRemove.filter((id) => id !== tagId) }
          : {
              ...prev,
              toAdd: [...prev.toAdd, tagId],
              toRemove: prev.toRemove.filter((id) => id !== tagId),
            };
      }
      return originallySelected
        ? {
            ...prev,
            toRemove: [...prev.toRemove, tagId],
            toAdd: prev.toAdd.filter((id) => id !== tagId),
          }
        : { ...prev, toAdd: prev.toAdd.filter((id) => id !== tagId) };
    });
  };

  const handleSave = async () => {
    try {
      for (const tagId of pendingChanges.toAdd) {
        await mutateChainAsync({ tagId, companyId: id });
      }

      for (const tagId of pendingChanges.toRemove) {
        await mutateUnchainAsync({ tagId, companyId: id });
      }
    } catch {
      // 失敗時はダイアログを開いたまま変更を保持し、ユーザーの編集内容を破棄しない
      showErrorToast('タグの保存に失敗しました。時間をおいて再度お試しください。');
      return;
    } finally {
      queryClient.invalidateQueries({ queryKey: ['Company', id] });
      queryClient.invalidateQueries({ queryKey: ['/agent/api/companies'] });
    }

    setIsOpen(false);
    setPendingChanges({ toAdd: [], toRemove: [] });
  };

  const handleOpen = () => {
    setIsOpen(true);
    setSelectedTagIds(tags.map((t) => t.id));
    setPendingChanges({ toAdd: [], toRemove: [] });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button color="secondary" size="sm" onClick={handleOpen}>
        <MdAdd className="size-4" />
        タグを追加
      </Button>
      {data && (
        <Dialog
          open={isOpen}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) handleClose();
          }}
        >
          <DialogContent className="flex max-h-[85vh] w-[90vw] max-w-[860px] flex-col rounded-sm p-0 shadow-layer3!">
            <div className="border-0 border-b border-solid border-border px-lg py-md">
              <DialogTitle className="text-heading-xs text-main">タグを編集</DialogTitle>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-lg">
              <div className="flex flex-wrap gap-x-xl gap-y-md">
                {data.tags?.map((tag) => (
                  <Checkbox
                    key={tag.id}
                    value={tag.id}
                    checked={selectedTagIds.includes(tag.id)}
                    disabled={isPending}
                    onChange={(checked) => handleTagToggle(tag.id, checked)}
                  >
                    <Tag color={tag.colorHex as TagColor}>{tag.name}</Tag>
                  </Checkbox>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-0 border-t border-solid border-border px-lg py-md">
              <Button color="tertiary" size="sm" asChild>
                <Link to="/agent/company_tags" onClick={handleClose}>
                  <MdSettings className="size-4 text-primary" />
                  <span className="text-primary">タグを管理</span>
                </Link>
              </Button>
              <div className="flex items-center gap-md">
                <Button type="button" color="secondary" size="md" onClick={handleClose}>
                  キャンセル
                </Button>
                <Button
                  type="button"
                  color="primary"
                  size="md"
                  onClick={handleSave}
                  disabled={!hasChanges || isPending}
                >
                  保存する
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
